/* __next_internal_client_entry_do_not_use__ TextArea auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { useTheme } from "styled-components";
import { Spacer } from "../../spacing";
import { Typography } from "../../Typography";
import { getDerivedPropsFromErrorProps } from "../error";
import { FormInputLabel, inputStyles } from "../index";
var InnerTextArea = styled.textarea.withConfig({
    componentId: "sc-13e1a709-0"
})([
    "",
    ";appearance:none;resize:",
    ";padding:8px 16px;color:",
    ";"
], inputStyles, function(props) {
    return props.$resizable ? "vertical" : "none";
}, function(props) {
    return props.disabled ? Core.text.disabled : Core.text.default;
});
var CharacterCount = styled(Typography).attrs({
    variant: "labelSansRegular"
}).withConfig({
    componentId: "sc-13e1a709-1"
})([
    "&&&{color:",
    ";text-align:end;position:relative;right:4px;}"
], function(props) {
    var _props_theme_input_helper, _props_theme;
    var _props_theme_input_helper_color;
    return (_props_theme_input_helper_color = (_props_theme = props.theme) === null || _props_theme === void 0 ? void 0 : (_props_theme_input_helper = _props_theme.input.helper) === null || _props_theme_input_helper === void 0 ? void 0 : _props_theme_input_helper.color) !== null && _props_theme_input_helper_color !== void 0 ? _props_theme_input_helper_color : Core.text.subdued;
});
var ErrorText = styled(Typography).attrs({
    variant: "labelSansRegular",
    color: Core.text.critical
}).withConfig({
    componentId: "sc-13e1a709-2"
})([
    "flex:1;"
]);
var HelperText = styled(Typography).attrs({
    variant: "labelSansRegular"
}).withConfig({
    componentId: "sc-13e1a709-3"
})([
    "&&&{color:",
    ";flex:1;}"
], function(props) {
    var _props_theme_input_helper, _props_theme;
    var _props_theme_input_helper_color;
    return (_props_theme_input_helper_color = (_props_theme = props.theme) === null || _props_theme === void 0 ? void 0 : (_props_theme_input_helper = _props_theme.input.helper) === null || _props_theme_input_helper === void 0 ? void 0 : _props_theme_input_helper.color) !== null && _props_theme_input_helper_color !== void 0 ? _props_theme_input_helper_color : Core.text.subdued;
});
/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/input/web/text-area-input)
 */ export var TextArea = /*#__PURE__*/ React.forwardRef(function(props, ref) {
    var _theme_input_label;
    var _props_value;
    var value = (_props_value = props.value) !== null && _props_value !== void 0 ? _props_value : "";
    var error = props.error, helper = props.helper, id = props.id, name = props.name, label = props.label, maxLength = props.maxLength, resizable = props.resizable, rest = _object_without_properties(props, [
        "error",
        "helper",
        "id",
        "name",
        "label",
        "maxLength",
        "resizable"
    ]);
    var theme = useTheme();
    var uniqueId = useUniqueId("text-area-input-");
    var _getDerivedPropsFromErrorProps = getDerivedPropsFromErrorProps({
        error: error
    }), showErrorState = _getDerivedPropsFromErrorProps.showErrorState, showErrorMessage = _getDerivedPropsFromErrorProps.showErrorMessage, errorMessage = _getDerivedPropsFromErrorProps.errorMessage;
    var message = null;
    if (showErrorMessage) {
        message = /*#__PURE__*/ _jsx(ErrorText, {
            children: errorMessage
        });
    } else if (helper) {
        message = /*#__PURE__*/ _jsx(HelperText, {
            children: helper
        });
    }
    var _theme_input_label1, _theme_input_label_color;
    return /*#__PURE__*/ _jsxs(Column, {
        style: {
            flex: 1,
            height: "inherit"
        },
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                style: {
                    height: "inherit"
                },
                children: [
                    label ? /*#__PURE__*/ _jsx(FormInputLabel, {
                        $typography: _object_spread_props(_object_spread({}, (_theme_input_label1 = theme.input.label) !== null && _theme_input_label1 !== void 0 ? _theme_input_label1 : {}), {
                            color: showErrorState ? Core.text.critical : (_theme_input_label_color = (_theme_input_label = theme.input.label) === null || _theme_input_label === void 0 ? void 0 : _theme_input_label.color) !== null && _theme_input_label_color !== void 0 ? _theme_input_label_color : Core.text.default
                        }),
                        htmlFor: id !== null && id !== void 0 ? id : uniqueId,
                        style: {
                            height: "inherit"
                        },
                        children: label
                    }) : null,
                    /*#__PURE__*/ _jsx(InnerTextArea, _object_spread({
                        $resizable: resizable,
                        $showErrorState: showErrorState,
                        id: id !== null && id !== void 0 ? id : uniqueId,
                        name: name,
                        maxLength: maxLength,
                        ref: ref
                    }, rest))
                ]
            }),
            maxLength || errorMessage || helper ? /*#__PURE__*/ _jsx(Spacer, {
                size: 4
            }) : null,
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                style: {
                    flexDirection: "row-reverse"
                },
                children: [
                    isNotUndefined(maxLength) ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(CharacterCount, {
                                theme: theme,
                                children: [
                                    value.length,
                                    "/",
                                    maxLength
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 10
                            })
                        ]
                    }) : null,
                    message
                ]
            })
        ]
    });
});
